import React, { useEffect, useState } from 'react';
import StatusPill from './StatusPill';

const clone = (value) => JSON.parse(JSON.stringify(value));
const slug = (value) => value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function newResource(unitId, index) {
  return { id: `${unitId || 'unit'}-resource-${index + 1}`, title: 'Nuevo recurso', url: 'https://', type: 'guide', required: true };
}

function newQuestion(unitId, index) {
  return {
    id: `${unitId || 'unit'}-q${index + 1}`,
    prompt: 'Nueva pregunta',
    options: [{ id: 'a', text: 'Opción A' }, { id: 'b', text: 'Opción B' }],
    correct_option_id: 'a',
    explanation: 'Explica por qué esta respuesta es correcta.',
  };
}

function newBlock(ownerId, index, type = 'text') {
  return {
    id: `${ownerId || 'content'}-block-${index + 1}`,
    type,
    title: 'Nuevo bloque',
    body: 'Describe aquí el contenido formativo.',
    ...(type === 'image' ? { url: 'https://', alt: 'Descripción de la imagen' } : {}),
    ...(type === 'video' ? { url: 'https://', transcript: 'Transcripción del video.' } : {}),
  };
}

function newUnit(index) {
  const id = `learning-unit-${index + 1}`;
  return {
    id,
    title: 'Nueva unidad',
    objective: 'Describe el resultado de aprendizaje observable.',
    concepts: ['concepto'],
    role_guidance: { common: 'Orientación aplicable a todos los roles.' },
    blocks: [newBlock(id, 0)],
    resources: [newResource(id, 0)],
    assessment: [newQuestion(id, 0)],
  };
}

async function jsonRequest(url, options = {}) {
  const response = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: options.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || 'No se pudo guardar el contenido.');
  return data;
}

export default function LearningAdmin({ apiUrl }) {
  const [document, setDocument] = useState(null);
  const [versions, setVersions] = useState({ published: null, draft: null });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [status, setStatus] = useState(null);
  const [section, setSection] = useState('course');
  const [previewRole, setPreviewRole] = useState('BUILDER');

  const load = async () => {
    try {
      const statusData = await jsonRequest(`${apiUrl}/api/admin/learning/status`);
      setStatus(statusData);
      if (!statusData.editable) {
        setVersions({ published: statusData.active_version, draft: null });
        setDocument(null);
        return;
      }
      const data = await jsonRequest(`${apiUrl}/api/admin/learning/draft`);
      setDocument(data.document);
      setVersions({ published: data.published_version, draft: data.draft_version });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const next = await jsonRequest(`${apiUrl}/api/admin/learning/sync`, { method: 'POST' });
      setStatus(next);
      setMessage({ type: 'success', text: next.available_version ? `Versión ${next.available_version} lista para activar.` : 'El currículo ya está actualizado.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    if (!status?.available_version) return;
    setBusy(true);
    try {
      const next = await jsonRequest(`${apiUrl}/api/admin/learning/activate/${status.available_version}`, { method: 'POST' });
      setStatus(next);
      setVersions({ published: next.active_version, draft: null });
      setMessage({ type: 'success', text: `Versión ${next.active_version} activada sin modificaciones locales.` });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [apiUrl]);

  const updateDocument = (field, value) => setDocument((prev) => ({ ...prev, [field]: value }));
  const updateUnit = (index, updater) => setDocument((prev) => {
    const next = clone(prev);
    updater(next.units[index]);
    return next;
  });
  const updateRoleTrack = (index, updater) => setDocument((prev) => {
    const next = clone(prev);
    updater(next.role_tracks[index]);
    return next;
  });
  const removeUnit = (index) => setDocument((prev) => ({ ...prev, units: prev.units.filter((_, i) => i !== index) }));
  const addUnit = () => setDocument((prev) => ({ ...prev, units: [...prev.units, newUnit(prev.units.length)] }));

  const save = async () => {
    setBusy(true);
    try {
      const data = await jsonRequest(`${apiUrl}/api/admin/learning/draft`, {
        method: 'PUT', body: JSON.stringify({ document }),
      });
      setDocument(data.document);
      setVersions((prev) => ({ ...prev, draft: data.draft_version }));
      setMessage({ type: 'success', text: 'Borrador de aprendizaje guardado.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!window.confirm(`¿Publicar la versión ${versions.draft}? El cambio será visible para toda la organización.`)) return;
    setBusy(true);
    try {
      await jsonRequest(`${apiUrl}/api/admin/learning/draft`, {
        method: 'PUT', body: JSON.stringify({ document }),
      });
      const data = await jsonRequest(`${apiUrl}/api/admin/learning/publish`, { method: 'POST' });
      setVersions({ published: data.published_version, draft: data.draft_version });
      setMessage({ type: 'success', text: `Versión ${data.published_version} publicada.` });
      await load();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <section className="forge-panel admin-panel learning-admin"><p>Cargando Academia FORGE…</p></section>;

  if (!status.editable) {
    return (
      <section className="forge-panel admin-panel learning-admin learning-admin-client">
        <div className="learning-admin-header">
          <div><div className="forge-section-eyebrow">Academia FORGE · Cuenta cliente</div><h2>Currículo oficial de solo lectura</h2><p>El contenido y la certificación pertenecen a NYXN. Esta instalación conserva progreso y evidencias localmente.</p></div>
          <StatusPill color="green">Activa v{status.active_version}</StatusPill>
        </div>
        {message && <div className={`admin-message ${message.type}`}>{message.text}</div>}
        <div className="learning-sync-grid">
          <div><span>Publicada</span><strong>{status.active_published_at ? new Date(status.active_published_at).toLocaleDateString('es-MX') : 'Sin fecha'}</strong></div>
          <div><span>Origen</span><strong>{status.active_source === 'nyxn' ? 'NYXN oficial' : status.active_source}</strong></div>
          <div><span>Integridad</span><strong>{status.active_integrity_hash?.slice(0, 12)}…</strong></div>
          <div><span>Sincronización</span><strong>{!status.sync_configured ? 'No configurada' : status.available_version ? `v${status.available_version} lista` : 'Al día'}</strong></div>
        </div>
        {!status.sync_configured && <div className="forge-inline-alert">Configura la URL central de Academia para buscar actualizaciones oficiales.</div>}
        <div className="learning-admin-actions">
          <button type="button" className="forge-button" disabled={busy || !status.sync_configured} onClick={sync}>Buscar actualización</button>
          <span />
          <button type="button" className="forge-button primary" disabled={busy || !status.available_version} onClick={activate}>Activar versión {status.available_version || ''}</button>
        </div>
      </section>
    );
  }

  if (!document) return <section className="forge-panel admin-panel learning-admin"><p>Cargando editor central…</p></section>;

  return (
    <section className="forge-panel admin-panel learning-admin">
      <div className="learning-admin-header">
        <div>
          <div className="forge-section-eyebrow">Módulo 01 · Aprender</div>
          <h2>Contenido y evaluaciones</h2>
          <p>Edita el borrador sin afectar la versión publicada.</p>
        </div>
        <div className="learning-admin-versions">
          <StatusPill color="green">Publicada v{versions.published}</StatusPill>
          <StatusPill color="amber">Borrador v{versions.draft}</StatusPill>
        </div>
      </div>
      {message && <div className={`admin-message ${message.type}`}>{message.text}</div>}

      <nav className="learning-admin-nav" aria-label="Secciones de Academia FORGE">
        {[
          ['course', 'Curso FORGE'],
          ['roles', 'Formación por rol'],
          ['media', 'Biblioteca multimedia'],
          ['evaluations', 'Evaluaciones y certificación'],
          ['preview', 'Vista previa'],
          ['versions', 'Versiones y publicación'],
        ].map(([value, label]) => <button type="button" className={section === value ? 'active' : ''} onClick={() => setSection(value)} key={value}>{label}</button>)}
      </nav>

      {section === 'course' && <><div className="learning-admin-basics admin-form">
        <label>Título<input value={document.title} onChange={(event) => updateDocument('title', event.target.value)} /></label>
        <label>Descripción<textarea value={document.description || ''} onChange={(event) => updateDocument('description', event.target.value)} /></label>
      </div>

      <div className="learning-admin-units">
        {document.units.map((unit, unitIndex) => (
          <details className="learning-admin-unit" key={unitIndex} open={unitIndex === 0}>
            <summary><span>{String(unitIndex + 1).padStart(2, '0')}</span><strong>{unit.title}</strong><small>{unit.id}</small></summary>
            <div className="learning-admin-unit-body">
              <div className="learning-admin-grid admin-form">
                <label>ID estable<input value={unit.id} onChange={(event) => updateUnit(unitIndex, (next) => { next.id = slug(event.target.value); })} /></label>
                <label>Título<input value={unit.title} onChange={(event) => updateUnit(unitIndex, (next) => { next.title = event.target.value; })} /></label>
                <label className="wide">Objetivo<textarea value={unit.objective} onChange={(event) => updateUnit(unitIndex, (next) => { next.objective = event.target.value; })} /></label>
                <label>Conceptos separados por coma<input value={unit.concepts.join(', ')} onChange={(event) => updateUnit(unitIndex, (next) => { next.concepts = event.target.value.split(',').map((item) => item.trim()).filter(Boolean); })} /></label>
                <label>Orientación común<textarea value={unit.role_guidance?.common || ''} onChange={(event) => updateUnit(unitIndex, (next) => { next.role_guidance = { ...(next.role_guidance || {}), common: event.target.value }; })} /></label>
              </div>
              <div className="learning-admin-order-actions">
                <button type="button" className="forge-button" disabled={unitIndex === 0} onClick={() => setDocument((prev) => { const next = clone(prev); [next.units[unitIndex - 1], next.units[unitIndex]] = [next.units[unitIndex], next.units[unitIndex - 1]]; return next; })}>Subir unidad</button>
                <button type="button" className="forge-button" disabled={unitIndex === document.units.length - 1} onClick={() => setDocument((prev) => { const next = clone(prev); [next.units[unitIndex + 1], next.units[unitIndex]] = [next.units[unitIndex], next.units[unitIndex + 1]]; return next; })}>Bajar unidad</button>
              </div>

              <div className="learning-admin-block">
                <div className="learning-admin-block-head"><h3>Bloques de contenido</h3><button type="button" className="forge-button" onClick={() => updateUnit(unitIndex, (next) => { next.blocks = [...(next.blocks || []), newBlock(next.id, (next.blocks || []).length)]; })}>Agregar bloque</button></div>
                {(unit.blocks || []).map((block, blockIndex) => (
                  <div className="learning-admin-resource admin-form" key={block.id || blockIndex}>
                    <label>ID<input value={block.id} onChange={(event) => updateUnit(unitIndex, (next) => { next.blocks[blockIndex].id = slug(event.target.value); })} /></label>
                    <label>Tipo<select value={block.type} onChange={(event) => updateUnit(unitIndex, (next) => { next.blocks[blockIndex] = { ...newBlock(next.id, blockIndex, event.target.value), id: block.id, title: block.title }; })}><option value="text">Texto</option><option value="heading">Encabezado</option><option value="callout">Llamada clave</option><option value="example">Ejemplo</option><option value="list">Lista</option><option value="image">Imagen</option><option value="video">Video</option><option value="link">Enlace</option><option value="download">Descargable</option></select></label>
                    <label className="wide">Título<input value={block.title || ''} onChange={(event) => updateUnit(unitIndex, (next) => { next.blocks[blockIndex].title = event.target.value; })} /></label>
                    <label className="wide">Contenido<textarea value={block.body || ''} onChange={(event) => updateUnit(unitIndex, (next) => { next.blocks[blockIndex].body = event.target.value; })} /></label>
                    {['image', 'video', 'link', 'download'].includes(block.type) && <label className="wide">URL HTTPS<input value={block.url || ''} onChange={(event) => updateUnit(unitIndex, (next) => { next.blocks[blockIndex].url = event.target.value; })} /></label>}
                    {block.type === 'image' && <label className="wide">Texto alternativo<input value={block.alt || ''} onChange={(event) => updateUnit(unitIndex, (next) => { next.blocks[blockIndex].alt = event.target.value; })} /></label>}
                    {block.type === 'video' && <label className="wide">Transcripción<textarea value={block.transcript || ''} onChange={(event) => updateUnit(unitIndex, (next) => { next.blocks[blockIndex].transcript = event.target.value; })} /></label>}
                    <div className="learning-admin-order-actions"><button type="button" className="forge-button" disabled={blockIndex === 0} onClick={() => updateUnit(unitIndex, (next) => { [next.blocks[blockIndex - 1], next.blocks[blockIndex]] = [next.blocks[blockIndex], next.blocks[blockIndex - 1]]; })}>↑</button><button type="button" className="forge-button" disabled={blockIndex === unit.blocks.length - 1} onClick={() => updateUnit(unitIndex, (next) => { [next.blocks[blockIndex + 1], next.blocks[blockIndex]] = [next.blocks[blockIndex], next.blocks[blockIndex + 1]]; })}>↓</button></div>
                    {(unit.blocks || []).length > 1 && <button type="button" className="forge-button danger" onClick={() => updateUnit(unitIndex, (next) => { next.blocks.splice(blockIndex, 1); })}>Quitar bloque</button>}
                  </div>
                ))}
              </div>

              <div className="learning-admin-block">
                <div className="learning-admin-block-head"><h3>Recursos</h3><button type="button" className="forge-button" onClick={() => updateUnit(unitIndex, (next) => { next.resources.push(newResource(next.id, next.resources.length)); })}>Agregar recurso</button></div>
                {unit.resources.map((resource, resourceIndex) => (
                  <div className="learning-admin-resource admin-form" key={resourceIndex}>
                    <label>ID<input value={resource.id} onChange={(event) => updateUnit(unitIndex, (next) => { next.resources[resourceIndex].id = slug(event.target.value); })} /></label>
                    <label>Título<input value={resource.title} onChange={(event) => updateUnit(unitIndex, (next) => { next.resources[resourceIndex].title = event.target.value; })} /></label>
                    <label className="wide">URL HTTPS<input value={resource.url} onChange={(event) => updateUnit(unitIndex, (next) => { next.resources[resourceIndex].url = event.target.value; })} /></label>
                    <label>Tipo<input value={resource.type || ''} onChange={(event) => updateUnit(unitIndex, (next) => { next.resources[resourceIndex].type = event.target.value; })} /></label>
                    <label className="learning-check"><input type="checkbox" checked={resource.required !== false} onChange={(event) => updateUnit(unitIndex, (next) => { next.resources[resourceIndex].required = event.target.checked; })} /> Obligatorio</label>
                    {unit.resources.length > 1 && <button type="button" className="forge-button danger" onClick={() => updateUnit(unitIndex, (next) => { next.resources.splice(resourceIndex, 1); })}>Quitar</button>}
                  </div>
                ))}
              </div>

              <div className="learning-admin-block">
                <div className="learning-admin-block-head"><h3>Evaluación</h3><button type="button" className="forge-button" onClick={() => updateUnit(unitIndex, (next) => { next.assessment.push(newQuestion(next.id, next.assessment.length)); })}>Agregar pregunta</button></div>
                {unit.assessment.map((question, questionIndex) => (
                  <div className="learning-admin-question admin-form" key={questionIndex}>
                    <label>ID<input value={question.id} onChange={(event) => updateUnit(unitIndex, (next) => { next.assessment[questionIndex].id = slug(event.target.value); })} /></label>
                    <label className="wide">Pregunta<textarea value={question.prompt} onChange={(event) => updateUnit(unitIndex, (next) => { next.assessment[questionIndex].prompt = event.target.value; })} /></label>
                    <div className="wide learning-admin-options">
                      {question.options.map((option, optionIndex) => (
                        <label key={`${option.id}-${optionIndex}`}>Opción {option.id.toUpperCase()}<input value={option.text} onChange={(event) => updateUnit(unitIndex, (next) => { next.assessment[questionIndex].options[optionIndex].text = event.target.value; })} /></label>
                      ))}
                    </div>
                    <label>Respuesta correcta<select value={question.correct_option_id} onChange={(event) => updateUnit(unitIndex, (next) => { next.assessment[questionIndex].correct_option_id = event.target.value; })}>{question.options.map((option) => <option key={option.id} value={option.id}>{option.id.toUpperCase()}</option>)}</select></label>
                    <label className="wide">Explicación<textarea value={question.explanation} onChange={(event) => updateUnit(unitIndex, (next) => { next.assessment[questionIndex].explanation = event.target.value; })} /></label>
                    {unit.assessment.length > 1 && <button type="button" className="forge-button danger" onClick={() => updateUnit(unitIndex, (next) => { next.assessment.splice(questionIndex, 1); })}>Quitar pregunta</button>}
                  </div>
                ))}
              </div>
              <button type="button" className="forge-button danger" onClick={() => removeUnit(unitIndex)}>Eliminar unidad</button>
            </div>
          </details>
        ))}
      </div></>}

      {section === 'roles' && <div className="learning-admin-units">
        {(document.role_tracks || []).map((track, trackIndex) => (
          <details className="learning-admin-unit" key={track.role} open={trackIndex === 0}>
            <summary><span>{String(trackIndex + 1).padStart(2, '0')}</span><strong>{track.title}</strong><small>{track.role}</small></summary>
            <div className="learning-admin-unit-body">
              <div className="learning-admin-grid admin-form">
                <label>Rol<input value={track.role} readOnly /></label>
                <label>Título<input value={track.title} onChange={(event) => updateRoleTrack(trackIndex, (next) => { next.title = event.target.value; })} /></label>
                {['purpose', 'authority', 'evidence', 'escalation'].map((field) => <label className="wide" key={field}>{({ purpose: 'Propósito', authority: 'Límite de autoridad', evidence: 'Evidencia esperada', escalation: 'Cuándo escalar' })[field]}<textarea value={track[field]} onChange={(event) => updateRoleTrack(trackIndex, (next) => { next[field] = event.target.value; })} /></label>)}
                <label className="wide">Responsabilidades<input value={track.responsibilities.join(', ')} onChange={(event) => updateRoleTrack(trackIndex, (next) => { next.responsibilities = event.target.value.split(',').map((item) => item.trim()).filter(Boolean); })} /></label>
              </div>
              <div className="learning-admin-block">
                <div className="learning-admin-block-head"><h3>Contenido del rol</h3><button type="button" className="forge-button" onClick={() => updateRoleTrack(trackIndex, (next) => { next.blocks.push(newBlock(next.id, next.blocks.length)); })}>Agregar bloque</button></div>
                {track.blocks.map((block, blockIndex) => <div className="learning-admin-resource admin-form" key={block.id}>
                  <label>Tipo<select value={block.type} onChange={(event) => updateRoleTrack(trackIndex, (next) => { next.blocks[blockIndex] = { ...newBlock(next.id, blockIndex, event.target.value), id: block.id, title: block.title }; })}><option value="text">Texto</option><option value="callout">Llamada clave</option><option value="image">Imagen</option><option value="video">Video</option><option value="link">Enlace</option></select></label>
                  <label>Título<input value={block.title} onChange={(event) => updateRoleTrack(trackIndex, (next) => { next.blocks[blockIndex].title = event.target.value; })} /></label>
                  <label className="wide">Contenido<textarea value={block.body || ''} onChange={(event) => updateRoleTrack(trackIndex, (next) => { next.blocks[blockIndex].body = event.target.value; })} /></label>
                  {['image', 'video', 'link'].includes(block.type) && <label className="wide">URL HTTPS<input value={block.url || ''} onChange={(event) => updateRoleTrack(trackIndex, (next) => { next.blocks[blockIndex].url = event.target.value; })} /></label>}
                  {block.type === 'image' && <label className="wide">Texto alternativo<input value={block.alt || ''} onChange={(event) => updateRoleTrack(trackIndex, (next) => { next.blocks[blockIndex].alt = event.target.value; })} /></label>}
                  {block.type === 'video' && <label className="wide">Transcripción<textarea value={block.transcript || ''} onChange={(event) => updateRoleTrack(trackIndex, (next) => { next.blocks[blockIndex].transcript = event.target.value; })} /></label>}
                  <div className="learning-admin-order-actions"><button type="button" className="forge-button" disabled={blockIndex === 0} onClick={() => updateRoleTrack(trackIndex, (next) => { [next.blocks[blockIndex - 1], next.blocks[blockIndex]] = [next.blocks[blockIndex], next.blocks[blockIndex - 1]]; })}>↑</button><button type="button" className="forge-button" disabled={blockIndex === track.blocks.length - 1} onClick={() => updateRoleTrack(trackIndex, (next) => { [next.blocks[blockIndex + 1], next.blocks[blockIndex]] = [next.blocks[blockIndex], next.blocks[blockIndex + 1]]; })}>↓</button></div>
                </div>)}
              </div>
              <div className="learning-admin-block">
                <div className="learning-admin-block-head"><h3>Evaluación del rol</h3><button type="button" className="forge-button" onClick={() => updateRoleTrack(trackIndex, (next) => { next.assessment.push(newQuestion(next.id, next.assessment.length)); })}>Agregar pregunta</button></div>
                {track.assessment.map((question, questionIndex) => <div className="learning-admin-question admin-form" key={question.id}>
                  <label className="wide">Pregunta<textarea value={question.prompt} onChange={(event) => updateRoleTrack(trackIndex, (next) => { next.assessment[questionIndex].prompt = event.target.value; })} /></label>
                  {question.options.map((option, optionIndex) => <label key={option.id}>Opción {option.id.toUpperCase()}<input value={option.text} onChange={(event) => updateRoleTrack(trackIndex, (next) => { next.assessment[questionIndex].options[optionIndex].text = event.target.value; })} /></label>)}
                  <label>Respuesta correcta<select value={question.correct_option_id} onChange={(event) => updateRoleTrack(trackIndex, (next) => { next.assessment[questionIndex].correct_option_id = event.target.value; })}>{question.options.map((option) => <option value={option.id} key={option.id}>{option.id.toUpperCase()}</option>)}</select></label>
                  <label className="wide">Explicación<textarea value={question.explanation} onChange={(event) => updateRoleTrack(trackIndex, (next) => { next.assessment[questionIndex].explanation = event.target.value; })} /></label>
                  {track.assessment.length > 1 && <button type="button" className="forge-button danger" onClick={() => updateRoleTrack(trackIndex, (next) => { next.assessment.splice(questionIndex, 1); })}>Quitar pregunta</button>}
                </div>)}
              </div>
            </div>
          </details>
        ))}
      </div>}

      {section === 'media' && <div className="learning-admin-library">
        <h3>Biblioteca multimedia</h3>
        <p>Imágenes y videos permanecen bajo control de NYXN e incluyen alternativas accesibles.</p>
        {[...(document.units || []), ...(document.role_tracks || [])].flatMap((owner) => (owner.blocks || []).filter((block) => ['image', 'video'].includes(block.type)).map((block) => ({ ...block, owner: owner.title }))).map((block) => <article key={block.id}><StatusPill color="blue">{block.type}</StatusPill><div><strong>{block.title}</strong><span>{block.owner}</span><small>{block.url}</small></div></article>)}
        {![...(document.units || []), ...(document.role_tracks || [])].some((owner) => (owner.blocks || []).some((block) => ['image', 'video'].includes(block.type))) && <div className="forge-empty">Agrega una imagen o video desde los bloques del curso.</div>}
      </div>}

      {section === 'evaluations' && <div className="learning-admin-summary-grid">
        <div><span>Evaluaciones del curso</span><strong>{document.units.reduce((total, unit) => total + unit.assessment.length, 0)}</strong></div>
        <div><span>Evaluaciones por rol</span><strong>{(document.role_tracks || []).reduce((total, track) => total + track.assessment.length, 0)}</strong></div>
        <div><span>Aprobación mínima</span><strong>80%</strong></div>
        <div><span>Certificación</span><strong>Formación + taller + examen</strong></div>
      </div>}

      {section === 'preview' && <div className="learning-admin-preview">
        <div className="forge-section-eyebrow">Vista de participante</div><h2>{document.title}</h2><p>{document.description}</p>
        <label>Previsualizar como rol<select value={previewRole} onChange={(event) => setPreviewRole(event.target.value)}>{(document.role_tracks || []).map((track) => <option value={track.role} key={track.role}>{track.title}</option>)}</select></label>
        <div>{document.units.map((unit, index) => <article key={unit.id}><span>{String(index + 1).padStart(2, '0')}</span><div><strong>{unit.title}</strong><p>{unit.objective}</p></div></article>)}</div>
        {document.role_tracks?.find((track) => track.role === previewRole) && <div className="learning-admin-role-preview"><strong>{document.role_tracks.find((track) => track.role === previewRole).title}</strong><p>{document.role_tracks.find((track) => track.role === previewRole).purpose}</p><small>{document.role_tracks.find((track) => track.role === previewRole).authority}</small></div>}
      </div>}

      {section === 'versions' && <div className="learning-admin-version-card">
        <h3>Publicación central NYXN</h3><p>Publicar crea una versión inmutable. Las instalaciones cliente podrán sincronizarla y activarla sin editarla.</p>
        <div><StatusPill color="green">Publicada v{versions.published}</StatusPill><StatusPill color="amber">Borrador v{versions.draft}</StatusPill></div>
      </div>}

      <div className="learning-admin-actions">
        <button type="button" className="forge-button" onClick={addUnit}>Agregar unidad</button>
        <span />
        <button type="button" className="forge-button" disabled={busy} onClick={save}>Guardar borrador</button>
        <button type="button" className="forge-button primary" disabled={busy} onClick={publish}>Guardar y publicar</button>
      </div>
    </section>
  );
}
